import React, { useRef, useEffect, useLayoutEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { applyActions, drawErase, drawStroke, getBase, loadImage } from './render';

// Antall mellomtilstander vi holder i minnet for rask angring (ca. 4,6 MB hver)
const MAX_SNAPSHOTS = 5;
// Zoom er relativ til «hele bildet» (1 = bildet passer akkurat i visningen)
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 8;
const VIEW_PADDING = 16;

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
const isPrefix = (shorter, longer) => shorter.every((action, i) => longer[i] === action);
const touchDistance = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
const touchCenter = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

const ColoringPage = forwardRef(({ imageUrl, imageAlt, actions, onNewAction, fillColor, zoom, onZoomChange, activeTool, brushSize }, ref) => {
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 896, height: 1280 });
  const [fitScale, setFitScale] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const currentPath = useRef([]);
  const gesture = useRef(null);
  const zoomAnchor = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  // Tegnetilstand: grunnbildet, handlingene som er tegnet på lerretet nå,
  // og mellomtilstander (antall handlinger -> ImageData) for rask angring.
  const baseImageRef = useRef(null);
  const appliedActionsRef = useRef([]);
  const snapshotsRef = useRef(new Map());
  const strokeSnapshotRef = useRef(null);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const scale = fitScale * zoom;
  const getContext = () => canvasRef.current.getContext('2d', { willReadFrequently: true });

  const saveSnapshot = (count, imageData) => {
    const snapshots = snapshotsRef.current;
    snapshots.set(count, imageData);
    while (snapshots.size > MAX_SNAPSHOTS) snapshots.delete(snapshots.keys().next().value);
  };

  const redrawFromScratch = (ctx, allActions) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.drawImage(baseImageRef.current, 0, 0);
    applyActions(ctx, allActions, getBase(baseImageRef.current));
    snapshotsRef.current.clear();
  };

  // Last grunnbildet én gang per side og tegn lagrede handlinger oppå
  useEffect(() => {
    let cancelled = false;
    setIsReady(false);
    loadImage(imageUrl).then((image) => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      baseImageRef.current = image;
      setImageSize({ width: image.naturalWidth, height: image.naturalHeight });
      redrawFromScratch(getContext(), actionsRef.current);
      appliedActionsRef.current = actionsRef.current;
      setIsReady(true);
    }).catch((error) => console.error(error));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  // Hold lerretet i takt med handlingslista: ny handling tegnes alene,
  // angre bruker en mellomtilstand når vi har den, ellers tegnes alt på nytt.
  useEffect(() => {
    if (!isReady) return;
    const applied = appliedActionsRef.current;
    if (actions === applied) return;
    const ctx = getContext();
    const { width, height } = ctx.canvas;

    if (actions.length > applied.length && isPrefix(applied, actions)) {
      saveSnapshot(applied.length, strokeSnapshotRef.current || ctx.getImageData(0, 0, width, height));
      if (strokeSnapshotRef.current) {
        // Streken er allerede tegnet live; tegn den på nytt fra rent utgangspunkt
        ctx.putImageData(strokeSnapshotRef.current, 0, 0);
        strokeSnapshotRef.current = null;
      }
      applyActions(ctx, actions.slice(applied.length), getBase(baseImageRef.current));
    } else if (actions.length < applied.length && isPrefix(actions, applied) && snapshotsRef.current.has(actions.length)) {
      ctx.putImageData(snapshotsRef.current.get(actions.length), 0, 0);
      for (const count of [...snapshotsRef.current.keys()]) {
        if (count > actions.length) snapshotsRef.current.delete(count);
      }
    } else {
      redrawFromScratch(ctx, actions);
    }
    appliedActionsRef.current = actions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, isReady]);

  // «Hele bildet»: regn ut skalaen som får bildet til å passe i visningen
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const measure = () => {
      const w = viewport.clientWidth - VIEW_PADDING * 2;
      const h = viewport.clientHeight - VIEW_PADDING * 2;
      if (w > 0 && h > 0) setFitScale(Math.min(w / imageSize.width, h / imageSize.height));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [imageSize]);

  // Etter zoom: rull slik at punktet eleven zoomet rundt blir liggende stille
  useLayoutEffect(() => {
    const anchor = zoomAnchor.current;
    if (!anchor) return;
    zoomAnchor.current = null;
    const rect = canvasRef.current.getBoundingClientRect();
    const viewport = viewportRef.current;
    viewport.scrollLeft += rect.left + anchor.fx * rect.width - anchor.clientX;
    viewport.scrollTop += rect.top + anchor.fy * rect.height - anchor.clientY;
  }, [scale]);

  const zoomAround = (newZoom, clientX, clientY) => {
    const target = clampZoom(newZoom);
    if (Math.abs(target - zoomRef.current) < 0.001) return;
    const rect = canvasRef.current.getBoundingClientRect();
    zoomAnchor.current = {
      clientX, clientY,
      fx: (clientX - rect.left) / rect.width,
      fy: (clientY - rect.top) / rect.height,
    };
    onZoomChange(target);
  };

  const zoomAroundCenter = (newZoom) => {
    const rect = viewportRef.current.getBoundingClientRect();
    zoomAround(newZoom, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  useImperativeHandle(ref, () => ({
    zoomTo: (z) => zoomAroundCenter(z),
    zoomToFit: () => { zoomAnchor.current = null; onZoomChange(1); },
  }));

  // Knip på styreflata (Chromebook sender det som Ctrl + hjul). Må være en
  // ikke-passiv lytter for å kunne hindre at hele nettsiden zoomer.
  useEffect(() => {
    const viewport = viewportRef.current;
    const onWheel = (event) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      // Et musehjul-hakk (deltaY ~100) skal ikke hoppe rett til maks: begrens hvert steg
      const delta = Math.max(-25, Math.min(25, event.deltaY));
      zoomAround(zoomRef.current * Math.exp(-delta * 0.01), event.clientX, event.clientY);
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  });

  const getCoords = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // Én desimal holder godt og gjør lagrede streker mye mindre
    return {
      x: Math.round((clientX - rect.left) * (canvas.width / rect.width) * 10) / 10,
      y: Math.round((clientY - rect.top) * (canvas.height / rect.height) * 10) / 10,
    };
  };

  const strokeWidthNow = () => brushSize / scale;

  const startPan = (clientX, clientY) => {
    const viewport = viewportRef.current;
    gesture.current = { type: 'pan', x: clientX, y: clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
  };

  const movePan = (clientX, clientY) => {
    const g = gesture.current;
    if (!g || g.type !== 'pan') return;
    viewportRef.current.scrollLeft = g.left - (clientX - g.x);
    viewportRef.current.scrollTop = g.top - (clientY - g.y);
  };

  const startTool = (clientX, clientY) => {
    if (activeTool === 'pan') { startPan(clientX, clientY); return; }
    if (!isReady) return;
    const canvas = canvasRef.current;
    const { x, y } = getCoords(clientX, clientY);
    if (activeTool === 'fill') {
      if (x >= 0 && y >= 0 && x < canvas.width && y < canvas.height) {
        onNewAction({ type: 'fill', x: Math.round(x), y: Math.round(y), color: fillColor });
      }
    } else if (isStrokeTool) {
      strokeSnapshotRef.current = getContext().getImageData(0, 0, canvas.width, canvas.height);
      setIsDrawing(true);
      currentPath.current = [{ x, y }];
      paintSegment(currentPath.current);
    }
  };

  // Blyant og viskelær tegnes live bit for bit; handlingen lagres når streken er ferdig
  const isStrokeTool = activeTool === 'pencil' || activeTool === 'eraser';
  const paintSegment = (points) => {
    if (activeTool === 'eraser') drawErase(getContext(), { points, width: strokeWidthNow() }, baseImageRef.current);
    else drawStroke(getContext(), { points, color: fillColor, width: strokeWidthNow() });
  };

  const continueStroke = (clientX, clientY) => {
    const { x, y } = getCoords(clientX, clientY);
    currentPath.current.push({ x, y });
    paintSegment(currentPath.current.slice(-2));
  };

  const endStroke = () => {
    if (isDrawing && currentPath.current.length > 0) {
      const points = [...currentPath.current];
      onNewAction(activeTool === 'eraser'
        ? { type: 'erase', points, width: strokeWidthNow() }
        : { type: 'draw', points, color: fillColor, width: strokeWidthNow() });
    }
    currentPath.current = [];
    setIsDrawing(false);
  };

  const cancelStroke = () => {
    if (strokeSnapshotRef.current) getContext().putImageData(strokeSnapshotRef.current, 0, 0);
    strokeSnapshotRef.current = null;
    currentPath.current = [];
    setIsDrawing(false);
  };

  // Mus: flytting fortsetter selv om pekeren går utenfor bildet
  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    startTool(event.clientX, event.clientY);
    if (activeTool === 'pan') {
      const onMove = (e) => movePan(e.clientX, e.clientY);
      const onUp = () => {
        gesture.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
  };

  const handleMouseMove = (event) => {
    if (isDrawing && isStrokeTool) continueStroke(event.clientX, event.clientY);
  };

  const handleMouseUp = () => {
    if (isDrawing) endStroke();
  };

  // Berøring: én finger bruker verktøyet, to fingre zoomer og flytter.
  // touch-action: none på lerretet gjør at nettleseren ikke ruller selv.
  const handleTouchStart = (event) => {
    const t = event.touches;
    if (t.length === 2) {
      if (isDrawing) cancelStroke();
      gesture.current = { type: 'pinch', distance: touchDistance(t), center: touchCenter(t), zoom: zoomRef.current };
    } else if (t.length === 1) {
      startTool(t[0].clientX, t[0].clientY);
    }
  };

  const handleTouchMove = (event) => {
    const t = event.touches;
    const g = gesture.current;
    if (t.length === 2 && g && g.type === 'pinch') {
      const center = touchCenter(t);
      viewportRef.current.scrollLeft -= center.x - g.center.x;
      viewportRef.current.scrollTop -= center.y - g.center.y;
      g.center = center;
      zoomAround(g.zoom * (touchDistance(t) / g.distance), center.x, center.y);
    } else if (t.length === 1) {
      if (g && g.type === 'pan') movePan(t[0].clientX, t[0].clientY);
      else if (isDrawing && isStrokeTool) continueStroke(t[0].clientX, t[0].clientY);
    }
  };

  const handleTouchEnd = (event) => {
    // touchend er ikke passiv: dette hindrer at nettleseren i tillegg sender
    // et etterlignet museklikk (som ville gitt en ekstra fylling)
    event.preventDefault();
    if (event.touches.length === 0) {
      gesture.current = null;
      if (isDrawing) endStroke();
    }
  };

  const cssWidth = imageSize.width * scale;
  const cssHeight = imageSize.height * scale;

  return (
    <div className="viewport" ref={viewportRef}>
      <div className="stage" style={{ padding: VIEW_PADDING }}>
        <canvas
          ref={canvasRef}
          className={`page-canvas tool-${activeTool}`}
          role="img"
          aria-label={imageAlt}
          style={{ width: cssWidth, height: cssHeight, visibility: fitScale ? 'visible' : 'hidden' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        />
      </div>
    </div>
  );
});

export default ColoringPage;
